/* ---------- BS-001's site tags to IEC 81346-2's classes (#1231, the outside review; the engineer's verdict there) ----------
 * The cooling unit is an object emitting coldness (EQ), not heating (EP); the gas detector senses a substance's concentration (BQ),
 * not a position (BG); the UPS is a universal power supply, AC and DC in, AC out (TC), not a plain converter (TB). The tags come from the battery site's data; a save made with the old ones names them in its wire ends, its
 * wiring list and its readings. The save migration in packages/progress uses this (format 11); nothing else does.
 * Self-contained on purpose: the migration imports this file alone, not BS-001's pack. */

/** Each old tag and its new one, as the site names them (without the leading dash). */
export const BS001_TAG_RENAME: Readonly<Record<string, string>> = { "GB1-EP1": "GB1-EQ1", "GB1-BG1": "GB1-BQ1", "GB1-TB10": "GB1-TC1" };

/** The busbar's one tag (#1886, item 4 and 7; the owner's decision): the distribution block was -XC1 in the panel, the wiring list and
 *  page 2, and -WC1 on page 3's taps. XC is not a class for it (IEC 81346-2:2009 "not used"); it is a terminal-type connecting object for
 *  low voltage power, class XD, and takes the next free number, -XD4 (-XD0 to -XD3 unchanged). Saves name only -XC1 (page 3's taps are
 *  kept by their ids, never by tag). The save migration in packages/progress uses this (format 14). */
export const BS001_BUSBAR_RENAME: Readonly<Record<string, string>> = { XC1: "XD4" };

/** An old tag wherever it stands as a whole tag: "GB1-EP1:L1", "-GB1-BG1", "GB1-TB10-OUT:L", a wiring list's "GB1-BG1:11|XD3:3|…" key. Never inside
 *  a longer tag ("GB1-EP10" keeps its own). */
const renamer = (map: Readonly<Record<string, string>>) => {
  const old = new RegExp(`(?<![\\w])(${Object.keys(map).join("|")})(?![\\w])`, "g");
  return (s: string) => s.replace(old, t => map[t]);
};

function walk(v: any, renamed: (s: string) => string): any {
  if (typeof v === "string") return renamed(v);
  if (Array.isArray(v)) return v.map(x => walk(x, renamed));
  if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).map(([k, x]) => [renamed(k), walk(x, renamed)]));
  return v;
}

/** A saved BS-001 project ({ model, ui }, the model as a JSON string or an object, as saved) with the new tags of `map` (format 11's
 *  by default). */
export function renameBs001Save<T extends { model?: any; ui?: any }>(s: T, map: Readonly<Record<string, string>> = BS001_TAG_RENAME): T {
  const renamed = renamer(map), asText = typeof s.model === "string", model = walk(asText ? JSON.parse(s.model) : s.model ?? null, renamed);
  return { ...s, model: asText ? JSON.stringify(model) : model, ui: walk(s.ui ?? {}, renamed) };
}

/** A saved BS-001 project with the busbar's new tag (format 14): its own work and each earlier attempt it keeps ("Start again", #1220). */
export function renameBs001Busbar<T extends { model?: any; ui?: any; attempts?: any[] }>(s: T): T {
  const out = renameBs001Save(s, BS001_BUSBAR_RENAME);
  return Array.isArray(s.attempts) ? { ...out, attempts: s.attempts.map(a => renameBs001Save(a, BS001_BUSBAR_RENAME)) } : out;
}
